'use client'

import NextLink from 'next/link'
import type { MouseEvent } from 'react'
import { cx } from '@/app/lib/utils'
import styles from './Link.module.scss'
import type { LinkProps } from './types'

/**
 * Link [atom] — DS Entradas.
 *
 * Action link en color de texto (no azul). El subrayado se anima al hacer hover.
 * - Con `href`: navegación (`next/link`, o `<a target="_blank">` si `external`).
 * - Sin `href` y con `onClick`: acción (`<button>`).
 *
 * Tamaños: XS=12px · S=14px · M=16px · L=18px (Montserrat Medium 500).
 */
export function Link(props: LinkProps) {
  const {
    children,
    size = 'm',
    underline = true,
    dark = false,
    disabled = false,
    ariaLabel,
    className,
  } = props

  const rootClass = cx(
    styles.link,
    styles[`link--${size}`],
    underline ? styles['link--underline'] : styles['link--noUnderline'],
    dark && styles['link--dark'],
    disabled && styles['link--disabled'],
    className,
  )

  // Acción: solo onClick, sin href → <button>
  if (props.href === undefined) {
    return (
      <button
        type="button"
        className={rootClass}
        onClick={disabled ? undefined : props.onClick}
        disabled={disabled}
        aria-label={ariaLabel}
      >
        {children}
      </button>
    )
  }

  const handleClick = (
    event: MouseEvent<HTMLAnchorElement>,
  ): void => {
    if (disabled) {
      event.preventDefault()
      return
    }
    props.onClick?.(event)
  }

  // Navegación externa: <a> con rel seguro
  if (props.external) {
    return (
      <a
        href={props.href}
        target="_blank"
        rel="noopener noreferrer"
        className={rootClass}
        onClick={handleClick}
        aria-disabled={disabled || undefined}
        aria-label={ariaLabel}
      >
        {children}
      </a>
    )
  }

  // Navegación interna: next/link
  return (
    <NextLink
      href={props.href}
      prefetch={props.prefetch}
      className={rootClass}
      onClick={handleClick}
      aria-disabled={disabled || undefined}
      aria-label={ariaLabel}
    >
      {children}
    </NextLink>
  )
}
